import React, {
  useEffect, useState, useRef,
  Suspense,
} from 'react';
import { ExLoader, LoaderSize, LoaderVariant } from '@boomi/exosphere';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import './BoomiAI.scss';
import { getUserProcesses as getUserProcessesFromApi } from './api-service';
import Util from '@services/util';
import Features from '@utils/features';
import AutocompleteHandler, { AutoCompleteRef } from '../../components/Autocomplete/AutocompleteHandler';
import debounce from '@utils/general/general';
import boomiAIIcon1 from '@assets/boomi-ai/ui-kit/agentGarden/ai-icon-1.svg';
import AgentGardenLink from './AgentGardenLink';
import BoomiAIPrompts from './BoomiAIPrompts';
import { useBoomiContext } from '../../context/BoomiContext';
import { useBoomiAgentFoundationContext } from '../../context/BoomiAgentFoundationContext';

const BoomiAIConversationPage = React.lazy(() => import(/* webpackChunkName: "BoomiAIConversationPage" */'./BoomiAIConversationPage'));

function BoomiAILogo() {
  return (
    <div className="boomi-ai-icon-container">
      <img
        src={boomiAIIcon1}
        alt="Boomi AI bot icon"
        aria-label="Boomi AI bot icon"
      />
    </div>
  );
}
interface BoomiAiProps {
  userDetails: any;
  // eslint-disable-next-line react/require-default-props
  disableInteraction?: boolean;
  // eslint-disable-next-line react/require-default-props
  referrer?: any;
  // eslint-disable-next-line react/require-default-props
  isEmbedded?: boolean; // Optional prop
}
export default function BoomiAi({
  userDetails, isEmbedded = false, referrer = 'unknown',
  disableInteraction = false,
}: BoomiAiProps) {
  const { user: userName, user } = userDetails;
  const bottomEl = useRef<null | HTMLDivElement>(null);
  const [t] = useTranslation('common');
  const [disabled, setDisabled] = useState(false);
  const [searchParams] = useSearchParams();
  const {
    isDarkMode,
    setIsDarkMode,
    setInitialUserPrompt,
    setProcessId,
    setUserName,
    placeHolderType,
    setShowConversationPage,
    showConversationPage,
    setIsEmbedded,
    setInputText,
    methodResponse,
  } = useBoomiContext();
  const { updateSelectedAgent } = useBoomiAgentFoundationContext();
  const inputRefHomePage = useRef<HTMLInputElement | null>(null);
  const [availableHeight, setAvailableHeight] = useState(172);
  const autoCompleteRef = useRef<AutoCompleteRef>(null);
  const isAgentFoundation = window.location.href.includes('home');
  const isConsumerExperience = window.location.href.includes('chat');

  const disableChat = (isDisabled: boolean) => {
    setDisabled(isDisabled);
  };

  useEffect(() => {
    // Find out if dark mode is enabled
    if (user) {
      setIsDarkMode(Features.isDarkModeEnabled(user));
    }
    setIsEmbedded(isEmbedded);
    // eslint-disable-next-line
  }, [user]);

  const setHeight = (height: any) => {
    // eslint-disable-next-line
    console.log('availableHeight ', availableHeight);
    setAvailableHeight(height);
  };

  const handleFocus = () => {
    if (inputRefHomePage.current) {
      // Focus on the input field
      // inputRefHomePage.current?.focus();
    }
  };

  useEffect(() => handleFocus());

  function enterText(inputText: any, processId: any, type: any) {
    if (referrer === 'home') {
      updateSelectedAgent({
        name: t('getting_started_boomi_gpt'),
      });
    }
    setInputText('');
    const trimmedInput = inputText.trim();
    // Return early if the input is empty
    if (trimmedInput.length === 0) {
      return;
    }
    if ((type === 'scribe') && autoCompleteRef.current) {
      autoCompleteRef.current.updateText(inputText);
      return;
    }
    setInitialUserPrompt(inputText);
    setProcessId(processId);
    setUserName(userName);
    setShowConversationPage(true);
  }

  // Handle gptPromptQuery parameter from URL
  useEffect(() => {
    const gptPromptQuery = searchParams.get('gptPromptQuery');
    if (gptPromptQuery && gptPromptQuery.trim()) {
      const sanitizedGoal = DOMPurify.sanitize(gptPromptQuery);
      enterText(sanitizedGoal, {}, 'url-param');
    }
  }, [searchParams]);

  const getUserProcesses = debounce(async (keyword: any, type: any, query: any) => {
    try {
      const result = await getUserProcessesFromApi(keyword, type, query);
      if (result) {
        return result;
      }
      return '';
    } catch (error) {
      // eslint-disable-next-line
      console.error('Error in getUserProcesses:', error);
      return ''; // Or handle the error as needed
    }
  }, 1500);

  const accountId = Util.getCookie('ACCOUNT_ID');
  const agentUrlSuffix = Util.isDevelopmentMode() ? '/agentGarden' : `#agentGarden;accountId=${
    accountId as string
  }`;
  const agentGardenUrl = `${window.location.pathname}${agentUrlSuffix}`;
  const backgroundClass = `${isDarkMode ? 'landing-page-background-dark' : 'landing-page-background'}
  ${isAgentFoundation ? 'mini-gpt' : ''} ${isConsumerExperience ? 'ce-mini-gpt' : ''}`;

  // @ts-ignore
  return (
    <div className={`boomi-ai ${!showConversationPage ? backgroundClass : ''}`}>
      <main>
        {!showConversationPage && (
          <div className={`ai-homepage-container ${isAgentFoundation ? 'mini-gpt' : ''} ${isConsumerExperience ? 'ce-mini-gpt' : ''}`}>
            <div
              className={(() => {
                const classes = ['landing-page'];
                if (isDarkMode) classes.push('landing-page__dark');
                if (isAgentFoundation) classes.push('mini-gpt');
                if (isConsumerExperience) classes.push('ce-mini-gpt');
                return classes.join(' ');
              })()}
              data-testid="empty-conversation"
            >
              {referrer !== 'home'
              && (
              <div className={`landing-page__subtext ${isEmbedded ? 'landing-page__subtext-embedded' : ''}`}>
                <BoomiAILogo />
                <AgentGardenLink agentGardenUrl={agentGardenUrl} />
              </div>
              )}
              {referrer === 'home'
              && (
              <div className={`landing-page__subtext ${isEmbedded ? 'landing-page__subtext-embedded' : ''}`}>
                <AgentGardenLink agentGardenUrl={agentGardenUrl} />
              </div>
              )}
              <div className="chat-input-container__home">
                <div className={`chat-input-container__home__outer ${
                  isAgentFoundation ? 'mini-gpt homepage-mini-gpt' : ''
                }`}
                >
                  <span
                    data-testid="boomi-gpt-chat-input"
                    className={[
                      'chat-input-container__home__outer__span',
                      isDarkMode && 'chat-input-container__home__outer__span__dark',
                      disabled && 'chat-input-container__home__outer__span__disabled',
                      isEmbedded && 'mini-gpt',
                      isAgentFoundation && 'homepage-mini-gpt',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    <AutocompleteHandler
                      ref={autoCompleteRef}
                      enterText={enterText}
                      disableChat={disableChat}
                      disabled={disabled}
                      getUserProcesses={getUserProcesses}
                      isDarkMode={isDarkMode}
                      inputRef={inputRefHomePage}
                      handleFocus={handleFocus}
                      bottomEl={bottomEl}
                      setHeight={setHeight}
                      conversationPage={false}
                      placeHolderType={placeHolderType}
                      methodResponse={methodResponse}
                      disableInteraction={disableInteraction}
                    />
                  </span>
                </div>
              </div>
            </div>
            <BoomiAIPrompts
              enterText={enterText}
              darkMode={isDarkMode}
              methodResponse={methodResponse}
              disableInteraction={disableInteraction}
            />
          </div>
        )}

        {showConversationPage && (
          <Suspense fallback={(
            <div className="full-page-loader" data-testid="loader">
              <ExLoader size={LoaderSize.MEDIUM} variant={LoaderVariant.SPINNER} />
            </div>
            )}
          >
            <BoomiAIConversationPage referrer={referrer} />
          </Suspense>
        )}
      </main>
    </div>
  );
}
